import { VITE_REACT_APP_API_GRAPHQL } from "@/constants/variables";

/**
 * Login-free client for the public worker badge. It talks to the Express
 * `/badge` endpoint on the GraphQL server (NOT Apollo) so the page can render
 * for an anonymous scanner. The response shape mirrors `SafetyBadgeView` in
 * mela/libs/data-layer/.../safety-badge.controller.ts.
 *
 * The token is a credential, so it travels in the `Authorization` header and
 * never in a backend URL — a token in the path would be captured by access
 * logs. (It does ride in the page URL the QR encodes; see `tokenFromPathname`.)
 */

export type BadgeDocumentState = "VALID" | "EXPIRING" | "EXPIRED" | "MISSING";

export interface BadgeDocument {
  categoryLabel: string;
  title: string | null;
  state: BadgeDocumentState;
  hasDocument: boolean;
  expiryDate: string | null;
  attachmentMissing: boolean;
}

/** Name only — the payload deliberately carries no task ids. */
export interface BadgeTask {
  name: string;
}

export interface BadgeView {
  worker: {
    firstName: string;
    lastName: string | null;
    taxCode: string | null;
    /** Public-bucket URL for the worker photo; the token guards the card data, not the image. */
    photoUrl: string | null;
  };
  summary: "BLOCKED" | "WARNING" | "OK";
  documents: BadgeDocument[];
  /** Optional: this client can ship ahead of the API that emits it. */
  tasks?: BadgeTask[];
  expiresInSeconds: number;
}

export type BadgeResult =
  | { status: "ok"; view: BadgeView }
  | { status: "expired" }
  | { status: "invalid" }
  | { status: "rate_limited" }
  | { status: "error" };

const badgeApiBase = (): string =>
  (VITE_REACT_APP_API_GRAPHQL ?? "").replace(/\/graphql\/?$/, "");

/** `/badge` — never token-bearing; the token rides in the Authorization header. */
export const badgeEndpoint = (path = ""): string =>
  `${badgeApiBase()}/badge${path}`;

const authHeader = (token: string): Record<string, string> => ({
  Authorization: `Bearer ${token}`,
});

/** Browser fetch never times out on a stalled connection, so we impose a deadline. */
const REQUEST_TIMEOUT_MS = 10_000;

const isBadgeDocument = (value: unknown): value is BadgeDocument =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Record<string, unknown>).state === "string";

/**
 * Shape guard for the 200 body, covering everything the card dereferences
 * unconditionally — `summary.toLowerCase()`, `doc.state.toLowerCase()` and the
 * expiry computed from `expiresInSeconds` (a missing one yields NaN, which
 * renders a freshly fetched card as already stale). A payload that fails is a
 * transport error, so the scanner gets the retryable card instead of a throw.
 *
 * `tasks` is not required — an older API must still render a valid card — but
 * it is type-checked when present: `?? []` catches null, not a wrong type, and
 * `tasks: "none"` would reach `tasks.map` and blank the page.
 */
const isBadgeView = (value: unknown): value is BadgeView => {
  const candidate = value as Partial<BadgeView> | null | undefined;

  return (
    typeof candidate?.worker?.firstName === "string" &&
    typeof candidate.summary === "string" &&
    Number.isFinite(candidate.expiresInSeconds) &&
    Array.isArray(candidate.documents) &&
    candidate.documents.every(isBadgeDocument) &&
    (candidate.tasks === undefined ||
      candidate.tasks === null ||
      Array.isArray(candidate.tasks))
  );
};

export async function fetchBadge(
  token: string,
  lng?: string,
): Promise<BadgeResult> {
  if (!token) return { status: "invalid" };
  const query = lng ? `?lng=${encodeURIComponent(lng)}` : "";

  // `AbortSignal.timeout` is missing from the Safari versions still inside this
  // app's browserslist, so the deadline is driven by a controller we own.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const res = await fetch(badgeEndpoint(query), {
      headers: { accept: "application/json", ...authHeader(token) },
      signal: controller.signal,
    });

    if (res.ok) {
      const parsed: unknown = await res.json();
      if (!isBadgeView(parsed)) return { status: "error" };
      return { status: "ok", view: parsed };
    }

    if (res.status === 410) return { status: "expired" };
    if (res.status === 429) return { status: "rate_limited" };
    if (res.status === 404) return { status: "invalid" };
    return { status: "error" };
  } catch {
    // Covers network failures, a malformed JSON body, and the abort fired by
    // the deadline above — all of which render as the retryable error card.
    return { status: "error" };
  } finally {
    clearTimeout(timeout);
  }
}

/** Pull the raw token out of `/badge/<token>` without needing a Router. */
export const tokenFromPathname = (pathname: string): string =>
  pathname.replace(/^\/badge\//, "").split(/[/?#]/)[0] ?? "";
